import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import sanitizeHtml from "../lib/sanitizeHtml";
import { Avatar, Username, VipBadge } from "./UserBadge";
import { useAuth } from "../context/AuthContext";
import { CARD_BACKGROUNDS } from "../constants/loadout";
import FaIcon from "./FaIcon";
import { Activity, Users, Crown, Flame, FileText, Calendar as CalendarIcon, User as UserIcon, Tag, Mail, ArrowRight, ChevronRight } from "lucide-react";
import ChatboxWidget from "./ChatboxWidget";
import { ServerStatusWidget, ServerStatusRow, ServerSummaryFooter } from "./ServerStatusWidget";
import ServerDetailsModal from "./ServerDetailsModal";
import RadioWidget from "./RadioWidget";
import { 
  PopularContributorsWidget, 
  TimeOnlineWidget, 
  RecentStatusesWidget, 
  ImportantTopicsWidget, 
  PromotedTopicsWidget 
} from "./CustomWidgets";
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useWidgetBuilder } from '../context/WidgetBuilderContext';
import WidgetManagerDrawer from './WidgetManagerDrawer';
import { SortableWidget, WidgetDropZone } from './WidgetBuilderHelpers';
import EditWidgetModal from './EditWidgetModal';
import { newWidget } from '../lib/WidgetConstants';
import { toast } from 'sonner';
import { useQueryClient, useMutation } from '@tanstack/react-query';

export default function Sidebar() {
  const { user } = useAuth();
  const { isEditing, activeDragItem, setActiveDragItem } = useWidgetBuilder();
  const queryClient = useQueryClient();
  
  const { data: widgetsData } = useQuery({
    queryKey: ["widgets"],
    queryFn: async () => (await api.get("/idg/widgets/public")).data,
    staleTime: 120000,
  });

  const [items, setItems] = useState([]);
  const [editingWidget, setEditingWidget] = useState(null);

  useEffect(() => {
    if (widgetsData) setItems(widgetsData);
  }, [widgetsData]);

  const reorderMutation = useMutation({
    mutationFn: async (newItems) => await api.post("/idg/widgets/reorder", newItems),
    onSuccess: () => {
      queryClient.invalidateQueries(["widgets"]);
      toast.success("Layout saved!");
    }
  });

  const createMutation = useMutation({
    mutationFn: async (newWidgetData) => await api.post("/idg/widgets", newWidgetData),
    onSuccess: () => queryClient.invalidateQueries(["widgets"])
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => await api.delete(`/idg/widgets/${id}`),
    onSuccess: () => queryClient.invalidateQueries(["widgets"])
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  if (!widgetsData) return <aside className="space-y-4" data-testid="sidebar"><div className="tf-surface tf-clip-card p-4 h-32 animate-pulse" /></aside>;

  const visible = items.filter((w) => {
    if (isEditing) return true;
    const v = w.settings?.visibility || "all";
    if (v === "all") return true;
    if (v === "guest") return !user;
    if (v === "user") return !!user;
    if (v === "vip") return user?.is_vip;
    if (v === "admin") return user?.role === "admin" || user?.role === "root";
    return true;
  });

  const sections = ["top", "middle", "bottom"];

  const handleDragStart = (event) => {
    const { active } = event;
    const isNew = active.data.current?.isNew;
    if (isNew) {
      setActiveDragItem({ isNew: true, type: active.data.current.type, title: active.data.current.label });
    } else {
      const w = items.find(i => i.id === active.id);
      if (w) setActiveDragItem(w);
    }
  };

  const handleDragEnd = (event) => {
    setActiveDragItem(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;
    const isNew = active.data.current?.isNew;

    let targetPos = over.data.current?.position;
    if (!targetPos) {
      const overWidget = items.find(i => i.id === overId);
      if (overWidget) targetPos = overWidget.position;
    }
    if (!targetPos) targetPos = "middle";

    if (isNew) {
      const widgetType = active.data.current.type;
      const nw = newWidget(targetPos, widgetType);
      nw.title = active.data.current.label;
      const inPos = items.filter(i => i.position === targetPos);
      nw.order = inPos.length > 0 ? Math.max(...inPos.map(i => i.order || 0)) + 1 : 0;
      
      toast.promise(createMutation.mutateAsync(nw), {
        loading: 'Adding block...',
        success: 'Block added!',
        error: 'Error adding block'
      });
      return;
    }

    if (activeId !== overId) {
      setItems((prev) => {
        const activeIndex = prev.findIndex(i => i.id === activeId);
        const overIndex = prev.findIndex(i => i.id === overId);
        
        let newItems = [...prev];
        
        if (overIndex >= 0) {
          const overWidget = prev[overIndex];
          newItems[activeIndex].position = overWidget.position;
          newItems = arrayMove(newItems, activeIndex, overIndex);
        } else {
          newItems[activeIndex].position = targetPos;
        }

        const updatedPosItems = newItems.filter(i => i.position === targetPos);
        updatedPosItems.forEach((item, idx) => {
          const mainIdx = newItems.findIndex(i => i.id === item.id);
          if (mainIdx >= 0) newItems[mainIdx].order = idx;
        });

        reorderMutation.mutate(newItems.map(i => ({ id: i.id, position: i.position, order: i.order })));
        return newItems;
      });
    }
  };

  const handleEditSave = async (updatedWidget) => {
    try {
      await api.put(`/idg/widgets/${updatedWidget.id}`, updatedWidget);
      queryClient.invalidateQueries(["widgets"]);
      setEditingWidget(null);
      toast.success("Widget updated");
    } catch (e) {
      toast.error("Failed to update widget");
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <WidgetManagerDrawer />
      
      {editingWidget && (
        <EditWidgetModal 
          widget={editingWidget} 
          onClose={() => setEditingWidget(null)} 
          onSave={handleEditSave} 
        />
      )}

      <aside className="idg-sidebar" data-testid="sidebar">
        {sections.map((pos) => {
          const inPos = visible.filter((w) => w.position === pos).sort((a, b) => (a.order || 0) - (b.order || 0));
          return (
            <WidgetDropZone key={pos} id={pos}>
              <SortableContext items={inPos.map(i => i.id)} strategy={verticalListSortingStrategy}>
                {inPos.map((w) => (
                  <SortableWidget 
                    key={w.id} 
                    id={w.id} 
                    widget={w}
                    onEdit={setEditingWidget}
                    onDelete={(id) => {
                      if (window.confirm("Remove this widget?")) deleteMutation.mutate(id);
                    }}
                  >
                    <WidgetRenderer widget={w} user={user} />
                  </SortableWidget>
                ))}
              </SortableContext>
            </WidgetDropZone>
          );
        })}
        {visible.length === 0 && !isEditing && (
          <div className="tf-surface tf-clip-card p-4 text-xs tf-mono text-zinc-500 text-center">
            Niciun widget configurat.
          </div>
        )}
      </aside>

      <DragOverlay dropAnimation={null}>
        {activeDragItem ? (
          <div className="opacity-80 scale-105 pointer-events-none">
            {activeDragItem.isNew || !activeDragItem.id ? (
              <div className="bg-[var(--tf-primary)]/20 border border-[var(--tf-primary)] p-4 rounded text-white font-bold text-center">
                {activeDragItem.title}
              </div>
            ) : (
              <WidgetRenderer widget={activeDragItem} user={user} />
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
// end Sidebar replacement

export function WidgetRenderer({ widget, user }) {
  switch (widget.type) {
    case "stats": return <StatsWidget title={widget.title || "Live Stats"} />;
    case "top_users": return <TopUsersWidget title={widget.title || "Top Frags"} count={widget.settings?.items_count || 5} />;
    case "online_users": return <OnlineUsersWidget title={widget.title || "Online Now"} count={widget.settings?.items_count || 10} />;
    case "recent_posts": return <RecentPostsWidget title={widget.title || "Recent Activity"} count={widget.settings?.items_count || 5} showDate={widget.settings?.show_date} showAuthor={widget.settings?.show_author} />;
    case "profile": return user ? <ProfileWidget user={user} title={widget.title || "Your Profile"} /> : null;
    case "categories": return <CategoriesWidget title={widget.title || "Categories"} count={widget.settings?.items_count || 6} />;
    case "tags": return <TagsWidget title={widget.title || "Tags"} />;
    case "calendar": return <CalendarWidget title={widget.title || "Calendar"} />;
    case "html": return <HtmlWidget title={widget.title} html={widget.settings?.custom_html || ""} />;
    case "chatbox": return <ChatboxWidget currentBoardId="all" />;
    case "radio": return <RadioWidget title={widget.title || "Pescobar Radio"} />;
    case "popular_contributors": return <PopularContributorsWidget title={widget.title || "Popular Contributors"} />;
    case "time_online": return <TimeOnlineWidget title={widget.title || "Time Spent Online"} />;
    case "recent_statuses": return <RecentStatusesWidget title={widget.title || "Recent Status Updates"} />;
    case "important_topics": return <ImportantTopicsWidget title={widget.title || "Important topics"} />;
    case "promoted_topics": return <PromotedTopicsWidget title={widget.title || "Promoted topics"} />;
    case "server_status": return <ServerStatusBankWidget widget={widget} />;
    default: return null;
  }
}

export function CardWrap({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-xl" data-idg-collapse data-testid={testid}>
      <div className="bg-[#06080d] border-b border-[#141824] px-4 py-3 flex justify-between items-center cursor-pointer select-none hover:bg-[#0a0d14] transition-colors" data-idg-collapse-trigger>
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-[var(--tf-primary)]" />}
          <h4 className="font-chakra font-bold text-xs uppercase tracking-wider text-white">{title}</h4>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-500 transition-transform duration-200" />
      </div>
      <div className="p-3.5" data-idg-collapse-content>{children}</div>
    </div>
  );
}

function StatsWidget({ title }) {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data, staleTime: 60000 });
  const s = stats || {};
  return (
    <CardWrap title={title} icon={Activity} testid="widget-stats">
      <div className="text-xs tf-mono space-y-1.5 text-zinc-300">
        <div className="flex justify-between py-1 border-b border-[#121620]"><span>Membri:</span> <span className="text-[var(--tf-primary)] font-bold">{s.users || 0}</span></div>
        <div className="flex justify-between py-1 border-b border-[#121620]"><span>Topicuri:</span> <span className="text-[var(--tf-primary)]">{s.topics || 0}</span></div>
        <div className="flex justify-between py-1 border-b border-[#121620]"><span>Postări:</span> <span className="text-[var(--tf-primary)]">{s.posts || 0}</span></div>
        <div className="flex justify-between py-1"><span>Online Acum:</span> <span className="text-emerald-400 font-bold" translate="no">{s.online_now || 0}</span></div>
      </div>
    </CardWrap>
  );
}

function TopUsersWidget({ title, count }) {
  const { data: users } = useQuery({ queryKey: ["topUsers", count], queryFn: async () => (await api.get(`/users/top?limit=${count}`)).data, staleTime: 60000 });
  const uList = users || [];
  return (
    <CardWrap title={title} icon={Crown} testid="widget-top-users">
      <div className="space-y-1.5">
        {uList.map((u, i) => (
          <Link 
            key={u.id} 
            to={`/u/${u.username}`} 
            className="flex items-center gap-2.5 bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 p-2 transition-all group"
          >
            <span className={`text-[10px] font-mono w-4 text-center font-bold ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-amber-600' : 'text-zinc-500'}`}>
              {i === 0 ? '1.' : i === 1 ? '2.' : i === 2 ? '3.' : `${i + 1}.`}
            </span>
            <Avatar user={u} size={26} />
            <Username user={u} asText className="truncate text-xs font-bold text-zinc-300 group-hover:text-white" />
            <span className="ml-auto font-mono text-[10px] text-amber-400 font-bold">{u.points || 0}</span>
          </Link>
        ))}
      </div>
    </CardWrap>
  );
}

function OnlineUsersWidget({ title, count }) {
  const { data } = useQuery({ queryKey: ["onlineUsers"], queryFn: async () => (await api.get("/presence/online")).data, refetchInterval: 30000 });
  const d = data || { users: [], count: 0 };
  return (
    <CardWrap title={`${title} (${d.count})`} icon={Flame} testid="widget-online-users">
      <div className="flex flex-wrap gap-1.5">
        {d.users.slice(0, count).map((u) => (
          <Link key={u.id} to={`/u/${u.username}`} title={u.username} className="hover:scale-105 transition-transform">
            <Avatar user={u} size={32} />
          </Link>
        ))}
        {d.users.length === 0 && <span className="text-xs tf-mono text-zinc-500">Nimeni online.</span>}
      </div>
    </CardWrap>
  );
}

function RecentPostsWidget({ title, count, showDate = true, showAuthor = true }) {
  const { data: posts } = useQuery({ queryKey: ["recentPosts", count], queryFn: async () => (await api.get(`/posts/recent?limit=${count}`)).data, staleTime: 30000 });
  const pList = posts || [];
  return (
    <CardWrap title={title || "Postări recente"} icon={FileText} testid="widget-recent-posts">
      <div className="space-y-2">
        {pList.length === 0 && <p className="text-xs tf-mono text-zinc-500 text-center py-2">Nicio postare recentă.</p>}
        {pList.map((p) => {
          const auth = p.author || { username: p.author_username || "Membru" };
          const topicTitle = p.topic?.title || p.title || "Discuție Forum";
          const accentColor = auth.group_color || "var(--tf-primary)";
          return (
            <div 
              key={p.id} 
              className="flex items-center gap-3 p-2.5 bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-all group"
            >
              {/* Logo Avatar + Custom Frame */}
              <Link to={`/u/${auth.username}`} className="shrink-0 relative">
                <Avatar user={auth} size={36} hover={true} />
              </Link>

              {/* Post Details */}
              <div className="flex-1 min-w-0">
                <Link 
                  to={`/topic/${p.topic_id}`} 
                  className="text-xs font-bold text-white hover:text-[var(--tf-primary)] truncate block transition-colors leading-snug group-hover:text-[var(--tf-primary)]"
                  title={topicTitle}
                >
                  {topicTitle}
                </Link>

                <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 mt-0.5 flex-wrap">
                  <span className="text-zinc-500">de</span>
                  <Username 
                    user={auth} 
                    className="font-bold text-[11px]" 
                  />
                  <Link to={`/topic/${p.topic_id}`} className="transition-transform group-hover:translate-x-0.5">
                    <ArrowRight className="w-3 h-3 text-[var(--tf-primary)]" />
                  </Link>
                </div>

                {p.created_at && (
                  <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                    {new Date(p.created_at).toLocaleString('ro-RO', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </CardWrap>
  );
}

function ProfileWidget({ user, title }) {
  const accentColor = user.group_color || "#FF4F00";
  const publicUrl = process.env.PUBLIC_URL || "/forum";
  
  const cardBgOpt = CARD_BACKGROUNDS.find((b) => b.id === user.loadout?.card_background);
  const bannerImage = (user.cover_banner_url || user.banner_url)
    ? (user.cover_banner_url || user.banner_url)
    : (cardBgOpt && cardBgOpt.id !== "none" && cardBgOpt.style?.backgroundImage)
      ? cardBgOpt.style.backgroundImage
      : `${publicUrl}/banners/default_profile_banner.png`;

  return (
    <div className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl rounded-xl" data-testid="widget-profile">
      
      {/* 1. Sleek Compact Header */}
      <div className="px-3.5 py-2 border-b border-[#141824] bg-[#06080d] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaIcon name="fa-solid fa-id-card" className="text-xs text-[var(--tf-primary)]" />
          <span className="font-chakra font-black text-xs uppercase tracking-wider text-white">
            {title || "PROFILUL TĂU"}
          </span>
        </div>
        <span className="text-[9px] font-mono text-zinc-500">#{user.id?.slice ? user.id.slice(0, 6) : "001"}</span>
      </div>

      {/* 2. Compact Integrated Hero Banner + Avatar Row */}
      <div className="relative p-3 bg-[#06080d]/80 border-b border-[#141824] overflow-hidden">
        {/* Subtle background cover with dark overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-25 pointer-events-none filter blur-[1px]"
          style={{ backgroundImage: `url(${bannerImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080d] via-[#06080d]/90 to-transparent pointer-events-none" />

        <div className="relative z-10 flex items-center gap-3">
          {/* Avatar with glow & status indicator */}
          <div className="relative shrink-0">
            <div 
              className="p-0.5 rounded-full bg-[#06080d] border-2 shadow-lg"
              style={{ borderColor: accentColor }}
            >
              <Avatar user={user} size={48} />
            </div>
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#06080d] shadow-[0_0_6px_#10b981]" title="Online" />
          </div>

          {/* User Meta (Username, Badges, Role) */}
          <div className="flex-1 min-w-0">
            <Username user={user} className="text-sm font-chakra font-black tracking-wide text-white truncate block" asText />
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
              {((user.loadout?.banner_badge && user.loadout.banner_badge !== "none") || (user.vip_tier && user.vip_tier !== "none")) && (
                <VipBadge tier={user.loadout?.banner_badge || user.vip_tier} />
              )}
              {user.role === "root" && (
                <span className="px-1.5 py-0.2 text-[8px] font-black uppercase tracking-widest bg-amber-500 text-black font-chakra rounded">
                  ROOT
                </span>
              )}
              {user.role === "admin" && user.role !== "root" && (
                <span className="px-1.5 py-0.2 text-[8px] font-black uppercase tracking-widest bg-red-600 text-white font-chakra rounded">
                  ADMIN
                </span>
              )}
              {user.group_name && user.role !== "root" && user.role !== "admin" && (
                <span className="px-1.5 py-0.2 text-[8px] font-bold uppercase tracking-widest bg-black/80 text-zinc-300 border border-[#181e2b] font-chakra rounded" style={{ color: accentColor }}>
                  {user.group_name}
                </span>
              )}
            </div>
            {user.personal_quote && (
              <p className="text-[10px] text-zinc-400 font-sans italic truncate mt-0.5 max-w-[170px]">
                "{user.personal_quote}"
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 3. Compact 3-Column Stats Row */}
      <div className="grid grid-cols-3 divide-x divide-[#141824] bg-[#06080d] border-b border-[#141824] text-center py-1.5 px-1">
        <div className="px-1">
          <div className="flex items-center justify-center gap-1 text-[9px] text-zinc-500 font-chakra uppercase">
            <FaIcon name="fa-solid fa-star" className="text-amber-400 text-[8px]" /> Pct
          </div>
          <span className="text-xs font-black text-amber-400 font-chakra block">{user.points || 0}</span>
        </div>
        <div className="px-1">
          <div className="flex items-center justify-center gap-1 text-[9px] text-zinc-500 font-chakra uppercase">
            <FaIcon name="fa-solid fa-comments" className="text-cyan-400 text-[8px]" /> Msg
          </div>
          <span className="text-xs font-black text-white font-chakra block">{user.message_count || 0}</span>
        </div>
        <div className="px-1">
          <div className="flex items-center justify-center gap-1 text-[9px] text-zinc-500 font-chakra uppercase">
            <FaIcon name="fa-solid fa-bolt" className="text-orange-400 text-[8px]" /> Reacții
          </div>
          <span className="text-xs font-black text-white font-chakra block">{user.reaction_score || user.reputation || 0}</span>
        </div>
      </div>

      {/* 4. Compact Body (Details & Quick Actions) */}
      <div className="p-3 space-y-2">
        
        {/* Daily Bonus Button - Slim & Sleek */}
        <button
          onClick={async () => {
            try {
              const res = await api.post("/points/daily-checkin");
              toast.success(res.data?.message || "Ai primit +15 IDG Points!");
              window.location.reload();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Ai revendicat deja bonusul zilnic!");
            }
          }}
          className="w-full py-1.5 bg-gradient-to-r from-amber-500/15 via-amber-500/25 to-amber-500/15 hover:from-amber-500/30 hover:to-amber-500/30 border border-amber-500/40 hover:border-amber-400 text-amber-300 font-chakra font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-sm cursor-pointer rounded-md"
        >
          <FaIcon name="fa-solid fa-gift" className="text-amber-400 text-xs" />
          <span>REVENDICĂ +15 IDG ZILNIC</span>
        </button>

        {/* 2x2 Micro Details Grid */}
        <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
          <div className="bg-[#06080d] p-1 px-2 rounded border border-[#141824] flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-1">
              <FaIcon name="fa-solid fa-location-dot" className="text-rose-400 text-[9px]" /> Loc
            </span>
            <span className="font-bold text-zinc-300 truncate max-w-[60px]">{user.location || "INDUNGI"}</span>
          </div>

          <div className="bg-[#06080d] p-1 px-2 rounded border border-[#141824] flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-1">
              <FaIcon name="fa-solid fa-gamepad" className="text-amber-400 text-[9px]" /> Joc
            </span>
            <span className="font-bold text-amber-400 uppercase truncate max-w-[60px]">{user.favorite_game || "CS2"}</span>
          </div>

          <div className="bg-[#06080d] p-1 px-2 rounded border border-[#141824] flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-1">
              <FaIcon name="fa-solid fa-crosshairs" className="text-emerald-400 text-[9px]" /> Armă
            </span>
            <span className="font-bold text-cyan-400 truncate max-w-[60px]">{user.loadout?.favorite_weapon || user.favorite_weapon || "Deagle"}</span>
          </div>

          <div className="bg-[#06080d] p-1 px-2 rounded border border-[#141824] flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-1">
              <FaIcon name="fa-brands fa-steam" className="text-cyan-400 text-[9px]" /> Steam
            </span>
            {user.steam_id ? (
              <a href={`https://steamcommunity.com/profiles/${user.steam_id}`} target="_blank" rel="noreferrer" className="font-bold text-cyan-400 hover:underline">
                Conectat
              </a>
            ) : (
              <span className="text-zinc-600">Off</span>
            )}
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="grid grid-cols-2 gap-1.5 pt-0.5">
          <Link 
            to={`/u/${user.username}`} 
            className="py-1.5 bg-[#0c1018] hover:bg-[#121824] border border-[#181e2b] hover:border-[var(--tf-primary)]/60 text-white font-chakra font-bold uppercase tracking-wider text-[10px] flex items-center justify-center gap-1.5 transition-all shadow-sm rounded-md"
          >
            <FaIcon name="fa-solid fa-user" className="text-zinc-400 text-[9px]" />
            <span>PROFIL</span>
          </Link>
          <Link 
            to={`/messages?new=${user.username}`} 
            className="py-1.5 bg-[#0c1018] hover:bg-[#121824] border border-[#181e2b] hover:border-cyan-500/60 text-zinc-300 hover:text-cyan-300 font-chakra font-bold uppercase tracking-wider text-[10px] flex items-center justify-center gap-1.5 transition-all shadow-sm rounded-md"
          >
            <FaIcon name="fa-solid fa-envelope" className="text-zinc-400 text-[9px]" />
            <span>MESAJ</span>
          </Link>
        </div>

      </div>
    </div>
  );
}

function CategoriesWidget({ title, count }) {
  const { data: boards } = useQuery({ queryKey: ["boards"], queryFn: async () => (await api.get("/boards")).data, staleTime: 60000 });
  const bList = (boards || []).filter(b => !b.parent_id);
  return (
    <CardWrap title={title} icon={Tag} testid="widget-categories">
      <div className="space-y-1">
        {bList.slice(0, count).map((b) => (
          <Link key={b.id} to={`/forums/${b.id}`} className="block text-xs tf-mono text-zinc-300 hover:text-[var(--tf-primary)] truncate">
            › {b.name} <span className="text-zinc-500">({b.topic_count || 0})</span>
          </Link>
        ))}
      </div>
    </CardWrap>
  );
}

function TagsWidget({ title }) {
  return <CardWrap title={title} icon={Tag} testid="widget-tags"><p className="text-xs tf-mono text-zinc-500">Coming soon.</p></CardWrap>;
}

function CalendarWidget({ title }) {
  const today = new Date();
  return (
    <CardWrap title={title} icon={CalendarIcon} testid="widget-calendar">
      <div className="text-center">
        <div className="text-[10px] tf-section-title">{today.toLocaleString("default", { month: "long" })}</div>
        <div className="tf-heading text-3xl text-[var(--tf-primary)] font-bold">{today.getDate()}</div>
        <div className="text-[10px] tf-mono text-zinc-500">{today.toLocaleString("default", { weekday: "long" })}</div>
      </div>
    </CardWrap>
  );
}

function HtmlWidget({ title, html }) {
  return (
    <div className="tf-surface tf-clip-card p-4" data-testid="widget-html">
      {title && <h4 className="tf-section-title mb-2">{title}</h4>}
      <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
    </div>
  );
}

// Default servers shown when widget has no custom config
const DEFAULT_SERVERS = [
  { id: "srv_1", address: "209.38.247.243:27015", name: "OFFICIAL.INDUNGI.PRO - 2026 NEW MOD", game_type: "cs16", dns: "official.indungi.pro" },
  { id: "srv_2", address: "209.38.247.243:27016", name: "DOPE.INDUNGI.PRO - ROMANIAN LEGENDS", game_type: "cs16", dns: "dope.indungi.pro" },
  { id: "srv_3", address: "209.38.247.243:27017", name: "MIX.INDUNGI.PRO - OFFICIAL MIX SERVER", game_type: "cs16", dns: "mix.indungi.pro" },
  { id: "srv_4", address: "CrGC77X8W", name: "INDUNGI NETWORK DISCORD", game_type: "discord", dns: "discord.gg/CrGC77X8W" },
];

function ServerStatusBankWidget({ widget }) {
  const title = widget.title || "Statistici Servere Live";
  const [selectedServer, setSelectedServer] = useState(null);

  const { data: dbServers } = useQuery({
    queryKey: ["serversList"],
    queryFn: async () => (await api.get("/servers/list")).data,
    staleTime: 60000,
  });

  const servers = dbServers && dbServers.length > 0 ? dbServers : DEFAULT_SERVERS;

  return (
    <CardWrap title={title} icon={Activity} testid="widget-server-status">
      <div className="space-y-1.5">
        {servers.map((s, index) => (
          <ServerStatusRow 
            key={s.id || s.address} 
            server={s} 
            index={index} 
            onSelect={(srv) => setSelectedServer(srv)}
          />
        ))}
      </div>

      {/* Dynamic Summary Footer Bar */}
      <div className="mt-2.5">
        <ServerSummaryFooter servers={servers} />
      </div>

      {/* Details Modal */}
      {selectedServer && (
        <ServerDetailsModal 
          server={selectedServer} 
          onClose={() => setSelectedServer(null)} 
        />
      )}
    </CardWrap>
  );
}

